// Which queued photos and videos the page may hand to the native transfer queue (BL-2683, BL-2684, ADR 070 — Ingest
// Protocol § 7.6). Pure, so every reason an add stays on the foreground executor is testable without a device.
import type { MediaAdd, LotMedia } from './lot-state-media';

/** The native lanes that upload a lot's photos and videos (the names `native-transfer/lanes.ts` freezes). */
const LANE_NAME_OF = { photo: 'media-upload', video: 'video-upload' } as const;

/** Input to {@link planMediaHandoff}. */
export interface MediaHandoffInput {
  media: LotMedia;
  /** The lanes the native side lists. */
  lanes: readonly string[];
  /** Staged file ids of adds whose preview is staged beside them: a photo's pick-time thumbnail, a video's poster frame. */
  thumbnailsStaged: ReadonlySet<string>;
}

/**
 * Split a lot's queued adds into the ones native takes and the ones the foreground executor keeps. Native takes
 * a staged photo or video with its staged preview that is not already native's, when the build lists the lane for its
 * kind (an older build lists no `video-upload`). Files already in storage and adds with no staged preview stay.
 *
 * @param input - the lot's media, the native lanes and the staged thumbnails.
 * @returns the adds to hand off and the rest.
 */
export function planMediaHandoff(input: MediaHandoffInput): { handoff: MediaAdd[]; foreground: MediaAdd[] } {
  const handoff: MediaAdd[] = [];
  const foreground: MediaAdd[] = [];
  for (const add of input.media.adds) {
    const eligible =
      input.lanes.includes(LANE_NAME_OF[add.type]) &&
      !add.native &&
      add.stagedFileId != null &&
      add.storagePath == null &&
      input.thumbnailsStaged.has(add.stagedFileId);
    (eligible ? handoff : foreground).push(add);
  }
  return { handoff, foreground };
}
